import { VisibilityTypeProductsListEnum } from 'app/shared/enums/visibility-type-products-list.enum.model';
import { ActiveChannelDTO } from 'app/shared/models/channel.model';
import { EntityChild } from 'app/shared/models/entity-child.model';
import { HttpBasicRequest } from 'app/shared/models/http-basic-request.model';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { OrderedMediaDTO, MediaDTO } from '../../../shared/models/media.models';
import { AssortmentRuleDTO } from '../models/products/assortment-condition.models';
import { AssortmentDTO, AssortmentVM, AssortmentItemVM, AssortmentPutDto } from '../models/products/assortment.model';
import { BulkEditorVariantDTO, BulkEditorVariantVM } from '../models/products/bulk-editor-variant.model';
import { ImportedStatusEnum, JobDetailsDto, ProductToImportVM } from '../models/products/product-to-import.model';
import { ProductDTO, ProductItemVM, OrderedMediaResDTO, ProductVM, ProductTagDTO, ProductVariantVM, ProductForCollapseVM, OrderedMediaVm, PriceListVariantPriceDto } from '../models/products/product.models';
import { ProductPaletteLimitDTO } from '../models/products/stock-product-pallettes.models';
import { VariantFullDTO, VariantPricelistDTO, VariantVM, VariantPutDTO, VariantForCollapseVM } from '../models/products/variant.models';
import { ProductTier } from '../models/settings/tier.model';
import { ProductToImportDTO } from '../services/back-office/products.service';
import { PaletteLimitDTO } from 'app/shared/models/palette-limit.model';
import { PaletteVM } from '../models/common/palette.models';

export class ProductsMapper {

  public static ProductItemVM(prod: ProductDTO, requestResolution: number = 50): ProductItemVM {
    const result = new ProductItemVM({
      styleGroupId: prod.styleGroupId,
      description: prod.description,
      descriptionEnglish: prod.descriptionEnglish,
      descriptionFrench: prod.descriptionFrench,
      descriptionSpanish: prod.descriptionSpanish,
      hideAllMedia: prod.hideAllMedia,
      externalId: prod.externalId,
      gender: prod.gender,
      isContinuative: prod.isContinuative,
      isCustomizableWithPlayers: prod.isCustomizableWithPlayers,
      checked: false,
      code: prod.code,
      extCode: prod.extCode ?? undefined,
      id: prod.id,
      storeId: prod.storeId,
      title: prod.title,
      creationTime: prod.creationTime,
      lastModificationTime: prod.lastModificationTime,
      creatorId: prod.creatorId,
      lastModifierId: prod.lastModifierId,
      tags: prod.tags,
      quantityBuyIncrement: prod.quantityBuyIncrement,
      retailPrice: prod.pricesSummary?.maxRetailPrice ? '€ ' + (prod.pricesSummary?.minimumRetailPrice !== prod.pricesSummary?.maxRetailPrice ? (prod.pricesSummary?.minimumRetailPrice.toString() + '/' + prod.pricesSummary?.maxRetailPrice?.toString()) : prod.pricesSummary?.maxRetailPrice?.toString()) : ' - ',
      wholesalePrice: prod.pricesSummary?.maxWholesalePrice ? '€ ' + (prod.pricesSummary?.minimumWholesalePrice !== prod.pricesSummary?.maxWholesalePrice ? (prod.pricesSummary?.minimumWholesalePrice.toString() + '/' + prod.pricesSummary?.maxWholesalePrice) : prod.pricesSummary?.maxWholesalePrice) : ' - '
    });
    result.imagePath = UtilityFunctions.getRequestResolution(prod.media?.resolutions, requestResolution);
    // this.completeMapper(result, prod, requestResolution);
    return result;
  }

  public static ProductVM(prod: ProductDTO, media?: OrderedMediaResDTO[]): ProductVM {
    const result = new ProductVM({
      styleGroupId: prod.styleGroupId,
      hideAllMedia: prod.hideAllMedia,
      externalId: prod.externalId,
      gender: prod.gender,
      isContinuative: prod.isContinuative,
      isCustomizableWithPlayers: prod.isCustomizableWithPlayers,
      code: prod.code,
      id: prod.id,
      storeId: prod.storeId,
      title: prod.title,
      media: media ? media.map((el, index: number) => this.orderedMediaResVm(el, 200, index)) : [],
      creationTime: prod.creationTime,
      lastModificationTime: prod.lastModificationTime,
      creatorId: prod.creatorId,
      lastModifierId: prod.lastModifierId,
      tags: prod.tags.map(x => {
        x.checked = true;
        return new ProductTagDTO(x);
      }),
      description: this.fixDescription(prod.description),
      descriptionEnglish: this.fixDescription(prod.descriptionEnglish),
      descriptionFrench: this.fixDescription(prod.descriptionFrench),
      descriptionSpanish: this.fixDescription(prod.descriptionSpanish),
      tiers: prod.tiers?.map(x => new ProductTier({
        id: x.id,
        name: x.name,
        checked: false,
        description: x.description,
        storeChannelId: x.storeChannelId,
        isArchived: x.isArchived,
        includeVariantsWithoutTiers: x.includeVariantsWithoutTiers,
      })) ?? [],
      quantityBuyIncrement: prod.quantityBuyIncrement,
    });
    return result;
  }


  public static ProductDTO(prod: ProductItemVM | ProductVM): ProductDTO {
    const result = new ProductDTO({
      styleGroupId: prod.styleGroupId,
      hideAllMedia: prod.hideAllMedia,
      externalId: prod.externalId,
      gender: prod.gender,
      isContinuative: prod.isContinuative,
      isCustomizableWithPlayers: prod.isCustomizableWithPlayers,
      code: prod.code,
      id: prod.id,
      storeId: prod.storeId,
      title: prod.title,
      creationTime: prod.creationTime,
      lastModificationTime: prod.lastModificationTime,
      creatorId: prod.creatorId,
      lastModifierId: prod.lastModifierId,
      tags: prod.tags?.map(x => {
        x.checked = true;
        return new ProductTagDTO(x);
      }),
      description: this.fixDescription(prod.description),
      descriptionEnglish: this.fixDescription(prod.descriptionEnglish),
      descriptionFrench: this.fixDescription(prod.descriptionFrench),
      descriptionSpanish: this.fixDescription(prod.descriptionSpanish),
      quantityBuyIncrement: prod.quantityBuyIncrement,
      quantityMinimumPerOrder: prod.quantityMinimumPerOrder
    });
    return result;
  }

  public static ProductVariantVM(variant: VariantFullDTO): ProductVariantVM {
    const res = new ProductVariantVM({
      productCode: variant.productCode,
      productTitle: variant.productTitle,
      blockQuantityBasedReOrderVisibility: variant.blockQuantityBasedReOrderVisibility,
      productId: variant.productId,
      availableQuantity: variant.availableQuantity,
      code: variant.code,
      cost: variant.cost,
      ean: variant.ean,
      id: variant.id,
      imagePath: UtilityFunctions.fixImagePath((variant.media || [])[0]?.src),
      palette: variant.palette,
      size: variant.size,
      wholesalePrice: 0,
      visibleForPreOrder: variant.visibleForPreOrder,
      visibleForReOrder: variant.visibleForReOrder,
      quantityBuyIncrement: variant.quantityBuyIncrement,
      paletteLimit: variant.paletteLimit,
      priceComputation: variant.priceComputation,
      lastAppliedWholesaleDiscountPercentage: variant.lastAppliedWholesaleDiscountPercentage,
      tags: variant.tags,
    });
    return res;
  }

  public static VariantVM(variant: VariantFullDTO, media: OrderedMediaDTO[], pricelists: VariantPricelistDTO[], tags: ProductTagDTO[], labels: EntityChild[], categories: EntityChild[], purposes: EntityChild[], teams: EntityChild[]): VariantVM {
    const result = new VariantVM({
      productCode: variant.productCode,
      productTitle: variant.productTitle,
      blockQuantityBasedReOrderVisibility: variant.blockQuantityBasedReOrderVisibility,
      productId: variant.productId,
      availableQuantity: variant.availableQuantity,
      basicCollections: variant.basicCollections,
      creationTime: variant.creationTime,
      creatorId: variant.creatorId,
      cost: variant.cost,
      lastModificationTime: variant.lastModificationTime,
      lastModifierId: variant.lastModifierId,
      code: variant.code,
      ean: variant.ean,
      id: variant.id,
      blankCellsEndDate: variant.blankCellsEndDate,
      blankCellsDeliveryDate: variant.blankCellsDeliveryDate,
      media: media ? media.map((el, index: number) => this.orderedMediaVm(el, index)) : [],
      palette: variant.palette,
      visibleForPreOrder: variant.visibleForPreOrder,
      visibleForReOrder: variant.visibleForReOrder,
      size: variant.size,
      tiers: variant.tiers?.map(x => new ProductTier({
        id: x.id,
        name: x.name,
        checked: true,
        description: x.description,
        storeChannelId: x.storeChannelId,
        isArchived: x.isArchived,
        includeVariantsWithoutTiers: x.includeVariantsWithoutTiers,
      })) ?? [],
      tags: tags.map(x => new ProductTagDTO({ id: x.id, name: x.name, checked: true })) ?? [],
      labels: labels || [],
      categories: categories || [],
      purposes: purposes || [],
      teams: teams || [],
      imagePath: UtilityFunctions.fixImagePath((media || []).find(x => !!x)?.media?.src),
      // purchaseLimit: v.purchaseLimit,
      pricelists: pricelists,
      automaticDiscounts: variant.automaticDiscounts,
      quantityBuyIncrement: variant.quantityBuyIncrement,
      paletteLimit: variant.paletteLimit,
      lastAppliedWholesaleDiscountPercentage: variant.lastAppliedWholesaleDiscountPercentage,
    });
    return result;
  }

  public static AssortmentProductVM(prod: ProductDTO, order: number = null, requestResolution: number = 50, visibility?: { preOrder?: VisibilityTypeProductsListEnum, reOrder?: VisibilityTypeProductsListEnum; }): ProductForCollapseVM {
    const result = new ProductForCollapseVM({
      styleGroupId: prod.styleGroupId,
      description: this.fixDescription(prod.description),
      descriptionEnglish: this.fixDescription(prod.descriptionEnglish),
      descriptionFrench: this.fixDescription(prod.descriptionFrench),
      descriptionSpanish: this.fixDescription(prod.descriptionSpanish),
      gender: prod.gender,
      hideAllMedia: prod.hideAllMedia,
      externalId: prod.externalId,
      code: prod.code,
      extCode: prod.extCode ?? undefined,
      isCustomizableWithPlayers: prod.isCustomizableWithPlayers,
      id: prod.id,
      imgPath: UtilityFunctions.fixImagePath(UtilityFunctions.getRequestResolution(prod.media?.resolutions, requestResolution)),
      order: order,
      newOrd: order,
      oldOrd: order,
      price: (prod.pricesSummary?.minimumWholesalePrice !== prod.pricesSummary?.maxWholesalePrice
        ? `${prod.pricesSummary?.minimumWholesalePrice ?? 0}/${prod.pricesSummary?.maxWholesalePrice ?? 0}`
        : `${prod.pricesSummary?.maxWholesalePrice ?? 0}`),
      storeId: prod.storeId,
      title: prod.title,
      creationTime: prod.creationTime,
      lastModificationTime: prod.lastModificationTime,
      creatorId: prod.creatorId,
      lastModifierId: prod.lastModifierId,
      quantityBuyIncrement: prod.quantityBuyIncrement,
      variants: (prod.variants || [])
        .filter(v => (!visibility?.preOrder && !visibility?.reOrder) || (visibility.preOrder === VisibilityTypeProductsListEnum.PartiallyVisible && v.visibleForPreOrder) || (visibility.reOrder === VisibilityTypeProductsListEnum.PartiallyVisible && v.visibleForReOrder))
        .map(x => {
          const res = new VariantForCollapseVM({
            productCode: x.productCode,
            productTitle: x.productTitle,
            palette: x.palette,
            blockQuantityBasedReOrderVisibility: x.blockQuantityBasedReOrderVisibility,
            visibleForPreOrder: x.visibleForPreOrder,
            visibleForReOrder: x.visibleForReOrder,
            productId: prod.id,
            cost: x.cost,
            availableQuantity: x.availableQuantity,
            code: x.code,
            extCode: prod.extCode ?? undefined,
            ean: x.ean,
            id: x.id,
            imagePath: UtilityFunctions.fixImagePath((x.media || [])[0]?.src),
            paletteId: x.paletteId || x.palette?.id,
            extPaletteId: x.extPaletteId,
            sizeId: x.sizeId || x.size?.id,
            colorName: x.palette ? x.palette.externalColorCode + ' - ' + x.palette.description : ' - ',
            sizeName: x.size?.name || ' - ',
            sizeGroup: x.size?.group,
            wholesalePrice: 0,
            disabled: false,
            automaticDiscounts: x.automaticDiscounts,
            quantityBuyIncrement: x.quantityBuyIncrement,
            lastAppliedWholesaleDiscountPercentage: x.lastAppliedWholesaleDiscountPercentage,
            paletteLimit: x.paletteLimit
          });
          return res;
        })
    });
    return result;
  }

  public static AssortmentVariantVM(data: ProductVariantVM, productPaletteLimit?: ProductPaletteLimitDTO) {
    return new VariantForCollapseVM({
      productCode: data.productCode,
      productTitle: data.productTitle,
      blockQuantityBasedReOrderVisibility: data.blockQuantityBasedReOrderVisibility,
      availableQuantity: data.availableQuantity,
      code: data.code,
      colorName: data.palette?.externalColorCode + ' - ' + data.palette?.description,
      cost: data.cost,
      ean: data.ean,
      id: data.id,
      imagePath: data.imagePath,
      palette: data.palette,
      paletteId: data.palette?.id,
      extPaletteId: data.extPaletteId,
      productId: data.productId,
      sizeId: data.size?.id,
      sizeName: data.size?.name,
      sizeGroup: data.size?.group,
      visibleForPreOrder: data.visibleForPreOrder,
      visibleForReOrder: data.visibleForReOrder,
      wholesalePrice: data.wholesalePrice,
      productPaletteStockLimitId: productPaletteLimit?.productPaletteLimitId ?? null,
      limit: productPaletteLimit?.stockLimit ?? null,
      minimumQuantityForPreOrder: productPaletteLimit?.minimumQuantityForPreOrder,
      minimumQuantityForReOrder: productPaletteLimit?.minimumQuantityForReOrder,
      quantityBuyIncrement: data.quantityBuyIncrement,
      lastAppliedWholesaleDiscountPercentage: data.lastAppliedWholesaleDiscountPercentage,
      paletteLimit: new PaletteLimitDTO({
        id: productPaletteLimit?.productPaletteLimitId ?? null,
        minimumQuantityForPreOrder: productPaletteLimit?.minimumQuantityForPreOrder ?? null,
        minimumQuantityForReOrder: productPaletteLimit?.minimumQuantityForReOrder ?? null,
        paletteId: productPaletteLimit?.palette?.id ?? null,
        productId: productPaletteLimit?.productId ?? null,
        stockLimit: productPaletteLimit?.stockLimit ?? null,
      }),
      tags: data.tags,
    });
  }

  public static AssortmentVM(data: AssortmentDTO, assTier: ProductTier[], tags: ProductTagDTO[], media: OrderedMediaDTO[], channels: ActiveChannelDTO[]): AssortmentVM {
    const result = new AssortmentVM({
      description: data.description,
      id: data.id,
      showcaseId: data.showcaseId,
      media: media ? media.map((el, index: number) => this.orderedMediaVm(el, index)) : [],
      publishOnPreFrom: data.publishOnPreFrom,
      publishOnPreTo: data.publishOnPreTo,
      status: data.status,
      storeId: data.storeId,
      tags: tags.map(x => new ProductTagDTO({ id: x.id, name: x.name, checked: true })) ?? [],
      tiers: assTier.map(x => new ProductTier({
        isArchived: x.isArchived,
        includeVariantsWithoutTiers: x.includeVariantsWithoutTiers,
        id: x.id,
        name: x.name,
        checked: true,
        description: x.description,
        storeChannelId: x.storeChannelId,
      })) ?? [],
      title: data.title,
      type: data.type,
      visibleForReOrder: data.visibleForReOrder,
      visibleForPreOrder: data.visibleForPreOrder,
      creationTime: data.creationTime,
      lastModificationTime: data.lastModificationTime,
      creatorId: data.creatorId,
      lastModifierId: data.lastModifierId,
      channel: data.channel
        ? channels?.find(x => x.channel?.id === data.channel.id)?.channel
        : channels?.find(x => x.id === data.storeChannelId)?.channel ?? null,
      channelSelected: data.channel
        ? channels?.find(x => x.channel?.id === data.channel.id)?.channel
        : channels?.find(x => x.id === data.storeChannelId)?.channel ?? null,
      order: data.order ?? null,
    });
    return result;
  }

  public static AssortmentItemVM(data: AssortmentDTO, channels): AssortmentItemVM {
    const result = new AssortmentItemVM({
      description: data.description,
      id: data.id,
      // assortmentMedia: (data.assortmentMedia || []).map(x => new AssortmentMediaDTO(x)),
      media: null,
      publishOnPreFrom: data.publishOnPreFrom,
      publishOnPreTo: data.publishOnPreTo,
      status: data.status,
      storeId: data.storeId,
      title: data.title,
      type: data.type,
      visibleForReOrder: data.visibleForReOrder,
      visibleForPreOrder: data.visibleForPreOrder,
      imagePath: UtilityFunctions.fixImagePath((data.media || [])[0]?.src),
      creationTime: data.creationTime,
      lastModificationTime: data.lastModificationTime,
      creatorId: data.creatorId,
      lastModifierId: data.lastModifierId,
      channel: data.channel ? channels?.find(x => x.channel?.id === data.channel.id)?.channel : null,
      order: data.order ?? null
    });
    return result;
  }

  public static VariantPutDTO(variant: VariantVM): VariantPutDTO {
    const result = new VariantPutDTO({
      availableQuantity: variant.availableQuantity,
      blankCellsDeliveryDate: variant.blankCellsDeliveryDate,
      blankCellsEndDate: variant.blankCellsEndDate,
      ean: variant.ean,
      id: variant.id,
      blockQuantityBasedReOrderVisibility: variant.blockQuantityBasedReOrderVisibility,
      code: variant.code,
      cost: variant.cost,
      visibleForPreOrder: variant.visibleForPreOrder,
      visibleForReOrder: variant.visibleForReOrder,
    });
    return result;
  }

  public static orderedMediaVm(orderMedia: OrderedMediaDTO, index?: number): OrderedMediaVm {
    orderMedia.media.src = UtilityFunctions.fixImagePath(orderMedia?.media?.src);
    const result = new OrderedMediaVm({
      isCover: index === 0,
      media: orderMedia.media,
      order: orderMedia.order,
      validityDateFrom: orderMedia.validityDateFrom,
      validityDateTo: orderMedia.validityDateTo,
      visible: orderMedia.visible,
    });
    return result;
  }

  public static orderedMediaResVm(orderedMedia: OrderedMediaResDTO, requestResolution: number, index?: number): OrderedMediaVm {
    const result = new OrderedMediaVm({
      isCover: index === 0,
      media: new MediaDTO({
        category: orderedMedia.media.category,
        id: orderedMedia.media.id,
        name: orderedMedia.media.name,
        src: UtilityFunctions.fixImagePath(UtilityFunctions.getRequestResolution(orderedMedia?.media?.resolutions, requestResolution)),
        type: orderedMedia.media.type
      }),
      order: orderedMedia.order,
      validityDateFrom: orderedMedia.validityDateFrom,
      validityDateTo: orderedMedia.validityDateTo,
      visible: orderedMedia.visible,
    });
    return result;
  }

  public static AssortmentPutDto(assortment: AssortmentVM): AssortmentPutDto {
    const result = new AssortmentPutDto({
      order: assortment.order ?? null,
      description: assortment.description,
      title: assortment.title,
      type: assortment.type,
      visibleForPreOrder: assortment.visibleForPreOrder,
      visibleForReOrder: assortment.visibleForReOrder,
      channelId: assortment.channelSelected?.id || assortment.storeChannelId,
      showcaseId: assortment?.showcaseId
    });
    return result;
  }

  public static AssortmentEditRequest(assortment: AssortmentVM, cacheAssorment: AssortmentVM, baseUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    if (!UtilityFunctions.isMatch(this.AssortmentPutDto(assortment), this.AssortmentPutDto(cacheAssorment))) {
      result.push({ url: baseUrl + `/assortments/${assortment.id}`, method: 'put', body: this.AssortmentPutDto(assortment) });
    }
    return result;
  }

  public static tierAssociateRequest(tiers: ProductTier[], cacheTiers: ProductTier[], nodeId: number, baseUrl: string, nodeUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    tiers.filter((tier1) => !cacheTiers.some(tier2 => tier1.id === tier2.id)).forEach(tier => {
      result.push({ url: baseUrl + `/${nodeUrl}/${nodeId}/${nodeUrl === 'products' ? 'tiers' : 'tier'}/${tier.id}`, method: 'post', });
    });
    return result;
  }

  public static tierDeleteRequest(tiers: ProductTier[], cacheTiers: ProductTier[], nodeId: number, baseUrl: string, nodeUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    cacheTiers.filter((tier1) => !tiers.some(tier2 => tier1.id === tier2.id)).forEach(tier => {
      result.push({ url: baseUrl + `/${nodeUrl}/${nodeId}/${nodeUrl === 'products' ? 'tiers' : 'tier'}/${tier.id}`, method: 'delete', });
    });
    return result;
  }

  public static tagAssociateRequest(tag: ProductTagDTO[], cacheTag: ProductTagDTO[], nodeId: number, baseUrl: string, nodeUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    tag.filter((tag1) => !cacheTag.some(tag2 => tag1.id === tag2.id)).forEach(tag => {
      result.push({ url: baseUrl + `/${nodeUrl}/${nodeId}/tag/${tag.id}`, method: 'post', });
    });
    return result;
  }

  public static tagDeleteRequest(tag: ProductTagDTO[], cacheTag: ProductTagDTO[], nodeId: number, baseUrl: string, nodeUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    cacheTag.filter((tag1) => !tag.some(tag2 => tag1.id === tag2.id)).forEach(tag => {
      result.push({ url: baseUrl + `/${nodeUrl}/${nodeId}/tag/${tag.id}`, method: 'delete', });
    });
    return result;
  }
  public static basicKustomPaletteSetRequest(palette: PaletteVM, cachePalette: PaletteVM, productId: number, baseUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    if (palette?.id && (palette?.id !== cachePalette?.id)) {
      result.push({ url: `${baseUrl}/products/${productId}/customizable/set`, method: 'post', body: palette?.id });
    }
    return result;
  }

  public static basicKustomPaletteUnsetRequest(palette: PaletteVM, cachePalette: PaletteVM, productId: number, baseUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    if (!palette?.id && cachePalette?.id) {
      result.push({ url: `${baseUrl}/products/${productId}/customizable/unset`, method: 'post', });
    }
    return result;
  }

  public static variantEditRequest(variant: VariantPutDTO, cacheVariant: VariantPutDTO, baseUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    if (!UtilityFunctions.isMatch(variant, cacheVariant)) {
      if ((!variant.blankCellsDeliveryDate && variant.blankCellsEndDate) || (variant.blankCellsDeliveryDate && !variant.blankCellsEndDate)) {
        variant.blankCellsDeliveryDate = variant.blankCellsDeliveryDate;
        variant.blankCellsEndDate = variant.blankCellsEndDate;
      }
      result.push({ url: baseUrl + `/variants/${variant.id}`, method: 'put', body: variant });
    }
    return result;
  }

  static priceEditRequest(variantId: number, pricelists: VariantPricelistDTO[], cachePricelists: VariantPricelistDTO[], variant: VariantVM, baseUrl: string): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    pricelists.filter((pricelist1) => !cachePricelists.some(pricelist2 => pricelist1.retailPrice === pricelist2.retailPrice && pricelist1.wholesalePrice === pricelist2.wholesalePrice)).forEach(pricelist => {
      const body: PriceListVariantPriceDto = {
        retailPrice: pricelist.retailPrice,
        variantId: variantId,
        overrideWholesaleDiscountPercentage: variant.lastAppliedWholesaleDiscountPercentage,
      };
      result.push({ url: baseUrl + `/price-lists/${pricelist.priceList.id}/price`, method: 'put', body });
    });

    return result;
  }

  public static ruleRequest(rules: AssortmentRuleDTO[], assortmentId: number, baseUrl: string,): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    rules?.forEach(rule => {
      result.push({ url: baseUrl + '/assortments/' + assortmentId + '/assortment-rules' + (rule.id ? ('/' + rule.id) : ''), method: !rule.id ? 'post' : rule.toDelete ? 'delete' : 'put', body: rule });
    });
    return result;
  }

  public static addVariantsRequest(variants: number[], assortmentId: number, baseUrl: string,): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    if (variants?.length > 0)
      result.push({ url: baseUrl + '/assortments/' + assortmentId + '/add-variants-list', method: 'put', body: variants });
    return result;
  }

  public static removeVariantsRequest(variants: number[], assortmentId: number, baseUrl: string,): HttpBasicRequest[] {
    const result: HttpBasicRequest[] = [];
    if (variants?.length > 0)
      result.push({ url: baseUrl + '/assortments/' + assortmentId + '/delete-variants-list', method: 'put', body: variants });
    return result;
  }

  public static BulkEditorVariantVM(variant: BulkEditorVariantDTO): BulkEditorVariantVM {
    const result = new BulkEditorVariantVM(variant);
    result.overrideWholesaleDiscountPercentage = variant.lastAppliedWholesaleDiscountPercentage;
    result.sizeName = variant.size?.name;
    result.colorName = variant.palette?.externalColorCode + ' - ' + variant.palette?.description;
    return result;
  }

  public static ProductToImportVM(prod: ProductToImportDTO, storeBaseDiscount?: number): ProductToImportVM {
    const y = new ProductToImportVM(prod);
    y.productCode = prod.styleCode;
    y.productName = prod.styleName;
    y.productId = prod.modelId;
    y.wholesaleDiscountPercentage = !!prod.jobDetail ? (prod.jobDetail.wholesaleDiscountPercentage || storeBaseDiscount) : null;
    y.importedStatus = this.ImportedStatus(prod?.jobDetail);
    return y;
  }

  public static ImportedStatus(jobDetail: JobDetailsDto) {
    return !jobDetail?.jobId ? ImportedStatusEnum.notImported : !!jobDetail.error ? ImportedStatusEnum.failed : !!jobDetail.endTime ? ImportedStatusEnum.imported : ImportedStatusEnum.importing;
  }

  public static fixDescription(description: string): string {
    if (!description) description = '';
    const regex = new RegExp('<[A-Z,a-z]+>.*<\/[A-Z,a-z]+>');
    if (!!description && !regex.test(description)) description = '<p>' + description + '</p>';
    return description;
  }
}
